import { z } from 'zod';

import {
  adminDomainsResponseSchema,
  apiErrorSchema,
  configResponseSchema,
  createDomainBodySchema,
  currentUserResponseSchema,
  domainResponseSchema,
  domainsResponseSchema,
  updateDomainSettingsBodySchema,
  type TAdminDomain,
  type TCurrentUser,
  type TDomainConfig
} from '../shared';

import type { TPublicDomain } from '../shared';

import { pocketBaseClient } from './pocketbase';

const REQUEST_TIMEOUT_MS = 30_000;

export class ApiClientError extends Error {
  public readonly code: string;
  public readonly fields?: Record<string, string[]>;

  constructor(
    message: string,
    code = 'API_ERROR',
    fields?: Record<string, string[]>
  ) {
    super(message);
    this.code = code;
    this.fields = fields;
  }
}

interface IRequestApiInput<TSchema extends z.ZodTypeAny> {
  path: string;
  method?: string;
  token?: string;
  body?: unknown;
  responseSchema: TSchema;
}

const parseApiError = (payload: unknown): ApiClientError => {
  const parsedError = apiErrorSchema.safeParse(payload);
  if (!parsedError.success) {
    return new ApiClientError('Request failed.');
  }

  return new ApiClientError(
    parsedError.data.error.message,
    parsedError.data.error.code,
    parsedError.data.error.fields
  );
};

const requestApi = async <TSchema extends z.ZodTypeAny>({
  path,
  method = 'GET',
  token,
  body,
  responseSchema
}: IRequestApiInput<TSchema>): Promise<z.infer<TSchema>> => {
  // Generous relative to the 10s upstream deadlines: a claim chains a
  // PocketBase insert, a Cloudflare create and a PocketBase patch, so a tighter
  // budget here would abort work that is still legitimately in flight.
  const response = await fetch(path, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS)
  }).catch((error: unknown) => {
    if (error instanceof Error && error.name === 'TimeoutError') {
      throw new ApiClientError(
        'The server took too long to respond.',
        'TIMEOUT'
      );
    }
    throw new ApiClientError('Unable to reach the server.', 'NETWORK_ERROR');
  });

  const payloadText = await response.text();
  let payload: unknown = {};

  if (payloadText) {
    try {
      payload = JSON.parse(payloadText) as unknown;
    } catch {
      throw new ApiClientError('Server returned invalid JSON.');
    }
  }

  if (!response.ok) {
    // An expired token otherwise leaves the UI "logged in" but unable to do
    // anything, with no way back to the login screen.
    if (response.status === 401) {
      pocketBaseClient.authStore.clear();
    }

    throw parseApiError(payload);
  }

  const parsedPayload = responseSchema.safeParse(payload);
  if (!parsedPayload.success) {
    throw new ApiClientError('Server returned an invalid response.');
  }

  return parsedPayload.data;
};

export const getPublicConfig = async (): Promise<TDomainConfig> => {
  const response = await requestApi({
    path: '/api/config',
    responseSchema: configResponseSchema
  });

  return response.data;
};

export const getCurrentUser = async (token: string): Promise<TCurrentUser> => {
  const response = await requestApi({
    path: '/api/me',
    token,
    responseSchema: currentUserResponseSchema
  });

  return response.data;
};

export const getDomains = async (token: string): Promise<TPublicDomain[]> => {
  const response = await requestApi({
    path: '/api/domains',
    token,
    responseSchema: domainsResponseSchema
  });

  return response.data;
};

export const getAdminDomains = async (
  token: string
): Promise<TAdminDomain[]> => {
  const response = await requestApi({
    path: '/api/admin/domains',
    token,
    responseSchema: adminDomainsResponseSchema
  });

  return response.data;
};

export const claimDomain = async (
  token: string,
  domain: string,
  targetIp: string,
  proxied: boolean
): Promise<TPublicDomain> => {
  const validatedPayload = createDomainBodySchema.parse({
    domain,
    targetIp,
    proxied
  });

  const response = await requestApi({
    path: '/api/domains',
    method: 'POST',
    token,
    body: validatedPayload,
    responseSchema: domainResponseSchema
  });

  return response.data;
};

export const updateDomainSettings = async (
  token: string,
  domainId: string,
  targetIp: string,
  proxied: boolean
): Promise<TPublicDomain> => {
  const validatedPayload = updateDomainSettingsBodySchema.parse({
    targetIp,
    proxied
  });

  const response = await requestApi({
    path: `/api/domains/${domainId}`,
    method: 'PATCH',
    token,
    body: validatedPayload,
    responseSchema: domainResponseSchema
  });

  return response.data;
};

export const deleteDomain = async (
  token: string,
  domainId: string
): Promise<TPublicDomain> => {
  const response = await requestApi({
    path: `/api/domains/${domainId}`,
    method: 'DELETE',
    token,
    responseSchema: domainResponseSchema
  });

  return response.data;
};
